
import { motion } from 'framer-motion';
import { useState } from 'react';

interface IntuneVaultFeature {
  icon: string;
  title: string;
  description: string;
}

interface IntuneVaultTrustBadge {
  icon: string;
  label: string;
}

interface IntuneVaultDashboardStat {
  label: string;
  icon: string;
}

interface IntuneVaultDashboardTab {
  icon: string;
  label: string;
}

interface IntuneVaultProject {
  id: string | number;
  name: string;
  description: string;
  language: string;
  stars: number;
  forks: number;
  url: string;
  linkedin?: {
    likes: number;
    comments: number;
    views: number;
    url: string;
  };
  version?: string;
  versionLabel?: string;
  isWebApp?: boolean;
  features?: IntuneVaultFeature[];
  trustBadges?: IntuneVaultTrustBadge[];
  dashboardStats?: IntuneVaultDashboardStat[];
  dashboardTabs?: IntuneVaultDashboardTab[];
}

const mockPolicyData = [
  { name: 'Windows Compliance - BitLocker', category: 'Compliance', platform: 'Windows', assignments: 12 },
  { name: 'iOS App Protection Policy', category: 'Configuration', platform: 'iOS', assignments: 8 },
  { name: 'Android Work Profile', category: 'Configuration', platform: 'Android', assignments: 5 },
  { name: 'macOS Security Baseline', category: 'Security', platform: 'macOS', assignments: 3 },
  { name: 'Windows Defender ATP', category: 'Security', platform: 'Windows', assignments: 15 },
  { name: 'Conditional Access - MFA', category: 'Settings Catalog', platform: 'Windows', assignments: 22 },
];

const mockAuditLogs = [
  { actor: 'admin@contoso.com', action: 'Policy Updated', target: 'Windows Compliance - BitLocker', time: '2 min ago', type: 'update' },
  { actor: 'john.doe@contoso.com', action: 'Policy Created', target: 'iOS App Protection v2', time: '18 min ago', type: 'create' },
  { actor: 'jane.smith@contoso.com', action: 'Policy Deleted', target: 'Legacy VPN Profile', time: '1 hr ago', type: 'delete' },
  { actor: 'admin@contoso.com', action: 'Assignment Changed', target: 'macOS Security Baseline', time: '3 hr ago', type: 'update' },
  { actor: 'it.ops@contoso.com', action: 'Policy Created', target: 'Android Work Profile v3', time: '5 hr ago', type: 'create' },
  { actor: 'john.doe@contoso.com', action: 'Policy Updated', target: 'Conditional Access - MFA', time: '1 day ago', type: 'update' },
];

const IntuneVaultCard = ({ project, index }: { project: IntuneVaultProject; index: number }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [auditSearch, setAuditSearch] = useState('');

  const filteredPolicies = mockPolicyData.filter(
    (p) => p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAuditLogs = mockAuditLogs.filter(
    (l) =>
      l.actor.toLowerCase().includes(auditSearch.toLowerCase()) ||
      l.action.toLowerCase().includes(auditSearch.toLowerCase()) ||
      l.target.toLowerCase().includes(auditSearch.toLowerCase())
  );

  const statValues = [47, 38, 9, 24, 15, 312];

  const actionColor = (type: string) => {
    if (type === 'create') return 'bg-emerald-500/20 text-emerald-400';
    if (type === 'delete') return 'bg-red-500/20 text-red-400';
    return 'bg-yellow-500/20 text-yellow-400';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.15, duration: 0.6 }}
      className="rounded-3xl overflow-hidden shadow-2xl"
    >
      {/* Header */}
      <div className="relative bg-gradient-to-br from-[#0a1628] via-[#0f2140] to-[#0a1628] px-8 pt-8 pb-6 text-center">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 30% 20%, rgba(59,130,246,0.3) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(59,130,246,0.2) 0%, transparent 50%)',
          }}
        />
        <div className="relative">
          <div className="flex items-center justify-center space-x-3 mb-2">
            <span className="text-3xl">🔐</span>
            <h2 className="text-3xl font-bold text-white tracking-tight">IntuneVault</h2>
          </div>
          <p className="text-gray-400 text-sm mb-3">Policy Management &amp; Comparison Tool</p>
          <div className="inline-flex items-center px-4 py-1.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full">
            <span className="text-emerald-400 text-xs font-semibold">
              {project.version} - {project.versionLabel} 🚀
            </span>
          </div>
        </div>
      </div>

      {/* v8.3 What's New Banner */}
      <div className="bg-gradient-to-r from-[#0f2140] via-[#162d55] to-[#0f2140] px-8 py-5 border-b border-white/10">
        <div className="flex items-center space-x-2 mb-3">
          <span className="px-2.5 py-0.5 bg-emerald-500/20 border border-emerald-500/40 rounded-full text-emerald-400 text-[10px] font-bold tracking-wider">
            v8.3 YENİLİKLER
          </span>
          <span className="text-gray-500 text-[10px]">Bu sürümle birlikte IntuneVault artık sadece &quot;ne olduğunu&quot; değil, &quot;ne değiştiğini&quot; de söylüyor</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {/* Audit Logs */}
          <div className="flex items-start space-x-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/8 transition-colors">
            <div className="w-9 h-9 flex items-center justify-center bg-amber-500/20 rounded-lg shrink-0">
              <i className="ri-file-search-line text-amber-400 text-lg"></i>
            </div>
            <div>
              <div className="flex items-center space-x-1.5 mb-1">
                <span className="text-white text-xs font-bold">🕵️ Integrated Audit Logs</span>
              </div>
              <p className="text-gray-400 text-[11px] leading-relaxed">
                Dashboard üzerinden Audit Log kayıtlarını çekiyoruz. Kim, neyi, ne zaman değiştirdi? Cevabı artık tek tık uzağınızda.
              </p>
            </div>
          </div>
          {/* Legacy Support */}
          <div className="flex items-start space-x-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/8 transition-colors">
            <div className="w-9 h-9 flex items-center justify-center bg-sky-500/20 rounded-lg shrink-0">
              <i className="ri-history-line text-sky-400 text-lg"></i>
            </div>
            <div>
              <div className="flex items-center space-x-1.5 mb-1">
                <span className="text-white text-xs font-bold">🕰️ Legacy Support</span>
                <span className="px-1.5 py-0.5 bg-sky-500/20 text-sky-400 text-[9px] font-semibold rounded">v8.0 &amp; v8.1</span>
              </div>
              <p className="text-gray-400 text-[11px] leading-relaxed">
                Önceki sürümlerdeki karşılaştırma ve snapshot verileri %100 uyumlu. Geçmiş verileriniz otomatik olarak yeni arayüze taşınıyor.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Landing Section */}
      <div className="relative bg-gradient-to-br from-[#1a3a6e] via-[#1e4080] to-[#1a3a6e] px-8 py-10">
        <div className="absolute inset-0 rounded-2xl mx-4 my-2 border border-white/10" />
        <div className="relative text-center mb-8">
          <h3 className="text-2xl font-bold text-white mb-3 flex items-center justify-center">
            <span className="mr-2">🚀</span>
            Enterprise-Grade Intune Management
          </h3>
          <p className="text-gray-300 text-sm max-w-xl mx-auto leading-relaxed">
            Comprehensive policy analytics, real-time monitoring, and advanced security insights — all in one powerful dashboard
          </p>
        </div>

        {/* Features Grid */}
        {project.features && (
          <div className="grid grid-cols-3 gap-3 mb-6">
            {project.features.slice(0, 6).map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.05 }}
                className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-4 text-center hover:bg-white/15 transition-all cursor-pointer group"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                  <i className={`${feature.icon} text-xl text-yellow-400`}></i>
                </div>
                <h4 className="text-white text-sm font-bold mb-1">{feature.title}</h4>
                <p className="text-gray-400 text-xs leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        )}

        {/* 7th Feature */}
        {project.features && project.features.length > 6 && (
          <div className="flex justify-center mb-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl p-4 text-center hover:bg-white/15 transition-all cursor-pointer group w-1/3"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                <i className={`${project.features[6].icon} text-xl text-emerald-400`}></i>
              </div>
              <h4 className="text-white text-sm font-bold mb-1">{project.features[6].title}</h4>
              <p className="text-gray-400 text-xs leading-relaxed">{project.features[6].description}</p>
            </motion.div>
          </div>
        )}

        {/* Sign in Button */}
        <div className="text-center mb-5">
          <a
            href={project.url}
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center px-8 py-3 bg-white hover:bg-gray-100 text-gray-900 font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-white/20"
          >
            <span className="mr-2">🔐</span>
            Sign in with Microsoft
          </a>
        </div>

        {/* Trust Badges */}
        {project.trustBadges && (
          <div className="flex items-center justify-center space-x-3">
            {project.trustBadges.map((badge, i) => (
              <div key={i} className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 rounded-full">
                <i className={`${badge.icon} text-sm text-gray-300`}></i>
                <span className="text-gray-300 text-xs font-medium">{badge.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dashboard Preview */}
      <div className="bg-gradient-to-br from-[#0a1628] via-[#0f1d35] to-[#0a1628] px-8 py-8">
        <div className="mb-6">
          <h4 className="text-white text-sm font-semibold mb-4 flex items-center">
            <i className="ri-dashboard-3-fill mr-2 text-yellow-400 w-4 h-4 flex items-center justify-center"></i>
            Dashboard Preview
          </h4>

          {/* Tabs */}
          {project.dashboardTabs && (
            <div className="flex space-x-2 mb-5">
              {project.dashboardTabs.map((tab, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === i
                      ? 'bg-[#1e4080] text-white border border-white/20'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 border border-transparent'
                  }`}
                >
                  <i className={`${tab.icon} text-sm`}></i>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              {project.dashboardStats && (
                <div className="grid grid-cols-6 gap-2 mb-5">
                  {project.dashboardStats.map((stat, i) => (
                    <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                      <div className="text-xl font-bold text-white mb-1">{statValues[i]}</div>
                      <div className="text-gray-500 text-[10px] leading-tight">{stat.label}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h5 className="text-gray-400 text-xs mb-3 flex items-center">
                    <i className="ri-pie-chart-fill mr-1.5 text-yellow-400"></i>
                    Policy Distribution
                  </h5>
                  <div className="flex items-center justify-center h-24">
                    <div className="relative w-20 h-20">
                      <div className="absolute inset-0 rounded-full border-4 border-emerald-500/60" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' }}></div>
                      <div className="absolute inset-0 rounded-full border-4 border-yellow-500/60" style={{ clipPath: 'polygon(50% 50%, 100% 0, 100% 50%)' }}></div>
                      <div className="absolute inset-0 rounded-full border-4 border-red-500/60" style={{ clipPath: 'polygon(50% 50%, 100% 50%, 100% 100%)' }}></div>
                      <div className="absolute inset-2 bg-[#0f1d35] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">47</span>
                      </div>
                    </div>
                    <div className="ml-4 space-y-1">
                      <div className="flex items-center text-[10px]">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 mr-1.5"></div>
                        <span className="text-gray-400">Compliance (18)</span>
                      </div>
                      <div className="flex items-center text-[10px]">
                        <div className="w-2 h-2 rounded-full bg-yellow-500 mr-1.5"></div>
                        <span className="text-gray-400">Configuration (15)</span>
                      </div>
                      <div className="flex items-center text-[10px]">
                        <div className="w-2 h-2 rounded-full bg-red-500 mr-1.5"></div>
                        <span className="text-gray-400">Security (14)</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-lg p-4">
                  <h5 className="text-gray-400 text-xs mb-3 flex items-center">
                    <i className="ri-bar-chart-horizontal-fill mr-1.5 text-emerald-400"></i>
                    Assignment Status
                  </h5>
                  <div className="space-y-2.5 mt-2">
                    {[
                      { label: 'Assigned', value: 38, pct: '81%', color: 'from-emerald-500 to-emerald-400', textColor: 'text-emerald-400' },
                      { label: 'Unassigned', value: 9, pct: '19%', color: 'from-yellow-500 to-yellow-400', textColor: 'text-yellow-400' },
                      { label: 'Security', value: 24, pct: '51%', color: 'from-red-500 to-red-400', textColor: 'text-red-400' },
                    ].map((item, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                          <span>{item.label}</span>
                          <span className={item.textColor}>{item.value}</span>
                        </div>
                        <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${item.color} rounded-full`} style={{ width: item.pct }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Policy Table */}
              <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                <div className="flex items-center space-x-2 px-4 py-2.5 border-b border-white/10">
                  <div className="flex-1 relative">
                    <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs"></i>
                    <input
                      type="text"
                      placeholder="Search policies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-md pl-7 pr-3 py-1.5 text-[10px] text-gray-300 placeholder-gray-500 focus:outline-none focus:border-white/30"
                    />
                  </div>
                  <div className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-md cursor-pointer hover:bg-emerald-500/30 transition-colors">
                    <i className="ri-download-2-line text-emerald-400 text-xs"></i>
                    <span className="text-emerald-400 text-[10px] font-medium whitespace-nowrap">Export CSV</span>
                  </div>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-[10px] text-gray-500 font-medium px-4 py-2">Policy Name</th>
                      <th className="text-left text-[10px] text-gray-500 font-medium px-4 py-2">Category</th>
                      <th className="text-left text-[10px] text-gray-500 font-medium px-4 py-2">Platform</th>
                      <th className="text-left text-[10px] text-gray-500 font-medium px-4 py-2">Assignments</th>
                      <th className="text-left text-[10px] text-gray-500 font-medium px-4 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPolicies.map((policy, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                        <td className="px-4 py-2 text-[10px] text-gray-300">{policy.name}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${
                            policy.category === 'Compliance' ? 'bg-emerald-500/20 text-emerald-400' :
                            policy.category === 'Configuration' ? 'bg-yellow-500/20 text-yellow-400' :
                            policy.category === 'Security' ? 'bg-red-500/20 text-red-400' :
                            'bg-sky-500/20 text-sky-400'
                          }`}>
                            {policy.category}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[10px] text-gray-400">{policy.platform}</td>
                        <td className="px-4 py-2 text-[10px] text-gray-300">{policy.assignments}</td>
                        <td className="px-4 py-2">
                          <button className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer">
                            <i className="ri-eye-line text-xs"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Comparison Tab */}
          {activeTab === 1 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <div className="bg-white/5 border border-white/10 rounded-lg p-5">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-white text-xs font-semibold flex items-center">
                    <span className="mr-1.5">📸</span>
                    Snapshot Management
                  </h5>
                  <div className="flex space-x-2">
                    <button className="flex items-center space-x-1 px-2.5 py-1.5 bg-emerald-500/20 border border-emerald-500/30 rounded-md text-emerald-400 text-[10px] font-medium cursor-pointer hover:bg-emerald-500/30 transition-colors whitespace-nowrap">
                      <i className="ri-add-line text-xs"></i>
                      <span>Create Snapshot</span>
                    </button>
                    <button className="flex items-center space-x-1 px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-md text-gray-400 text-[10px] font-medium cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap">
                      <i className="ri-save-line text-xs"></i>
                      <span>Export</span>
                    </button>
                    <button className="flex items-center space-x-1 px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-md text-gray-400 text-[10px] font-medium cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap">
                      <i className="ri-folder-open-line text-xs"></i>
                      <span>Import</span>
                    </button>
                  </div>
                </div>

                {/* Legacy Support Notice */}
                <div className="flex items-center space-x-2 bg-sky-500/10 border border-sky-500/20 rounded-lg px-3 py-2.5 mb-4">
                  <i className="ri-history-line text-sky-400 text-sm"></i>
                  <p className="text-sky-300 text-[10px]">
                    <strong>Legacy Support aktif:</strong> v8.0 &amp; v8.1 snapshot verileri %100 uyumlu — geçmiş verileriniz otomatik olarak yeni arayüze taşındı.
                  </p>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[
                    { val: 5, label: 'Added', color: 'emerald' },
                    { val: 2, label: 'Removed', color: 'red' },
                    { val: 8, label: 'Modified', color: 'yellow' },
                    { val: 32, label: 'Unchanged', color: 'white' },
                  ].map((item, i) => (
                    <div key={i} className={`bg-${item.color}-500/10 border border-${item.color}-500/20 rounded-lg p-3 text-center`}>
                      <div className={`text-lg font-bold text-${item.color}-400`}>{item.val}</div>
                      <div className="text-[10px] text-gray-400">{item.label}</div>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  {[
                    { date: 'Jan 15, 2025', count: 47 },
                    { date: 'Dec 28, 2024', count: 42 },
                  ].map((snap, i) => (
                    <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2.5 border border-white/10">
                      <div className="flex items-center space-x-3">
                        <input type="checkbox" className="rounded border-gray-600 cursor-pointer" defaultChecked />
                        <div>
                          <div className="text-gray-300 text-[11px] font-medium">Snapshot - {snap.date}</div>
                          <div className="text-gray-500 text-[10px]">{snap.count} policies captured</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-white cursor-pointer">
                          <i className="ri-download-2-line text-xs"></i>
                        </button>
                        <button className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-red-400 cursor-pointer">
                          <i className="ri-delete-bin-line text-xs"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 text-center">
                  <button className="flex items-center space-x-1.5 mx-auto px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-lg text-yellow-400 text-[11px] font-medium cursor-pointer hover:bg-yellow-500/30 transition-colors whitespace-nowrap">
                    <i className="ri-loop-left-line text-sm"></i>
                    <span>Compare Selected (2/2)</span>
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Audit Logs Tab */}
          {activeTab === 2 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
              <div className="bg-white/5 border border-white/10 rounded-lg overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                  <div className="flex items-center space-x-2">
                    <i className="ri-file-search-line text-amber-400 text-sm"></i>
                    <span className="text-white text-xs font-semibold">Audit Log Kayıtları</span>
                    <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[9px] font-bold rounded">NEW in v8.3</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-xs"></i>
                      <input
                        type="text"
                        placeholder="Kullanıcı veya işlem ara..."
                        value={auditSearch}
                        onChange={(e) => setAuditSearch(e.target.value)}
                        className="bg-white/5 border border-white/10 rounded-md pl-7 pr-3 py-1.5 text-[10px] text-gray-300 placeholder-gray-500 focus:outline-none focus:border-white/30 w-48"
                      />
                    </div>
                    <button className="flex items-center space-x-1 px-2.5 py-1.5 bg-white/5 border border-white/10 rounded-md text-gray-400 text-[10px] cursor-pointer hover:bg-white/10 transition-colors whitespace-nowrap">
                      <i className="ri-download-2-line text-xs"></i>
                      <span>Export</span>
                    </button>
                  </div>
                </div>

                {/* Log Rows */}
                <div className="divide-y divide-white/5">
                  {filteredAuditLogs.map((log, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors">
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className="w-7 h-7 flex items-center justify-center bg-white/10 rounded-full shrink-0">
                          <i className="ri-user-line text-gray-400 text-xs"></i>
                        </div>
                        <div className="min-w-0">
                          <div className="text-gray-300 text-[11px] font-medium truncate">{log.actor}</div>
                          <div className="text-gray-500 text-[10px] truncate">{log.target}</div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 shrink-0 ml-3">
                        <span className={`text-[9px] px-2 py-0.5 rounded font-semibold whitespace-nowrap ${actionColor(log.type)}`}>
                          {log.action}
                        </span>
                        <span className="text-gray-500 text-[10px] whitespace-nowrap">{log.time}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredAuditLogs.length === 0 && (
                  <div className="text-center py-8 text-gray-500 text-xs">Kayıt bulunamadı</div>
                )}

                <div className="px-4 py-2.5 border-t border-white/10 flex items-center justify-between">
                  <span className="text-gray-500 text-[10px]">{filteredAuditLogs.length} kayıt gösteriliyor</span>
                  <button className="text-amber-400 text-[10px] hover:text-amber-300 cursor-pointer whitespace-nowrap">
                    Tüm kayıtları gör →
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gradient-to-r from-[#0a1628] to-[#0f2140] px-8 py-5 border-t border-white/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-400">
            {project.linkedin && project.linkedin.likes > 0 && (
              <span className="flex items-center text-[#0A66C2]">
                <i className="ri-thumb-up-fill mr-1"></i>
                {project.linkedin.likes}
              </span>
            )}
            {project.linkedin && project.linkedin.comments > 0 && (
              <span className="flex items-center text-[#0A66C2]">
                <i className="ri-chat-3-fill mr-1"></i>
                {project.linkedin.comments}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {project.linkedin && project.linkedin.url && (
              <a
                href={project.linkedin.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center text-[#0A66C2] hover:text-[#4d9ae6] font-medium text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-linkedin-box-fill mr-1 text-base"></i>
                LinkedIn
              </a>
            )}
            <a
              href={project.url}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center px-5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line mr-1.5"></i>
              Siteyi Ziyaret Et
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default IntuneVaultCard;
